import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useGetRewardByIdQuery } from "../rewards.service";
import PageLoading from "src/components/pageLoading";
import { Timestamp } from "firebase/firestore";
import {
  FaArrowLeft,
  FaCheckCircle,
  FaTimesCircle,
  FaCalendarAlt,
  FaGlobeAmericas,
  FaTag,
  FaPen,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import { getRewardProviderMeta } from "../utils/provider-meta";
import { getRewardPayoutLabel } from "../utils/reward-display";
import { isTremendousRewardProductMetadata } from "../utils/tremendous";

const formatDate = (timestamp?: Timestamp) => {
  if (!timestamp) return "N/A";
  return timestamp.toDate().toLocaleDateString("en-US", {
    weekday: "short",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const RewardDetailsPage = () => {
  const { rewardId } = useParams<{ rewardId: string }>();
  const navigate = useNavigate();
  const { data: reward, isLoading, isError } = useGetRewardByIdQuery(rewardId!);
  const [showAllCountries, setShowAllCountries] = useState(false);

  if (isLoading) return <PageLoading />;

  if (isError || !reward) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <p className="text-gray-500 text-lg">Reward not found.</p>
        <button
          onClick={() => navigate("/rewards")}
          className="text-brand-500 hover:text-brand-600 font-medium text-sm cursor-pointer"
        >
          ← Back to Rewards
        </button>
      </div>
    );
  }

  const provider = getRewardProviderMeta(reward.provider.name);
  const payoutLabel = getRewardPayoutLabel(reward);
  const providerMetadata = reward.provider.productMetadata as Record<string, unknown> | undefined;
  const tremendousMetadata = isTremendousRewardProductMetadata(providerMetadata) ? providerMetadata : null;

  const MAX_COUNTRIES_VISIBLE = 12;
  const countries = reward.availableCountries || [];
  const visibleCountries = showAllCountries ? countries : countries.slice(0, MAX_COUNTRIES_VISIBLE);
  const hasMoreCountries = countries.length > MAX_COUNTRIES_VISIBLE;

  return (
    <div className="page_container space-y-5">
      {/* Top bar */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate("/rewards")}
          className="inline-flex items-center gap-2 text-gray-600 hover:text-brand-500 font-medium text-sm transition-colors cursor-pointer"
        >
          <FaArrowLeft className="w-3.5 h-3.5" />
          Back to Rewards
        </button>

        <button
          onClick={() => navigate(`/rewards/${reward.id}/edit`)}
          className="inline-flex items-center gap-2 bg-gray-900 hover:bg-brand-500 text-white px-4 py-2 rounded-lg font-semibold text-sm transition-colors cursor-pointer"
        >
          <FaPen className="w-3 h-3" />
          Edit Reward
        </button>
      </div>

      {/* Main card */}
      <div className="max-w-5xl mx-auto bg-white border border-gray-200 rounded-2xl overflow-hidden">
        {/* Top section: Image + Header */}
        <div className="grid grid-cols-1 lg:grid-cols-3">
          {/* Image */}
          <div className="lg:col-span-1">
            {reward.attachment?.fileUrl ? (
              <img
                src={reward.attachment.fileUrl}
                alt={reward.name}
                className="w-full h-full min-h-[260px] object-cover"
              />
            ) : (
              <div
                className={`w-full h-full min-h-[260px] flex items-center justify-center bg-gradient-to-br ${provider.gradient}`}
              >
                <span className="text-white/20 text-[90px]">
                  {provider.icon}
                </span>
              </div>
            )}
          </div>

          {/* Header info */}
          <div className="lg:col-span-2 p-6 flex flex-col justify-center">
            {/* Badges */}
            <div className="flex items-center gap-2 mb-3">
              {reward.isAvailable ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
                  <FaCheckCircle className="w-3 h-3" />
                  Available
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                  <FaTimesCircle className="w-3 h-3" />
                  Unavailable
                </span>
              )}

              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                <FaTag className="w-2.5 h-2.5" />
                {reward.category.charAt(0) + reward.category.slice(1).toLowerCase()}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {reward.name}
            </h1>

            {/* Description */}
            <p className="text-gray-500 text-sm leading-relaxed mb-5 max-w-xl">
              {reward.description}
            </p>

            {/* Key metrics */}
            <div className="flex flex-wrap gap-3">
              {/* Karma */}
              <div className="flex items-center gap-3 bg-brand-50 border border-brand-100 rounded-xl px-4 py-3 min-w-[160px]">
                <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
                  <img src="/karma-icon.png" alt="Karma" className="w-7 h-7 object-contain" />
                </div>
                <div>
                  <p className="text-[11px] text-brand-400 font-medium uppercase tracking-wider">Karma Cost</p>
                  <p className="text-xl font-bold text-brand-600 tabular-nums leading-tight">
                    {reward.amount?.toLocaleString() || 0}
                  </p>
                </div>
              </div>

              {/* Payout */}
              {payoutLabel && (
                <div className={`flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 min-w-[160px]`}>
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${provider.gradient} flex items-center justify-center text-white shadow-sm`}>
                    {provider.icon}
                  </div>
                  <div>
                    <p className="text-[11px] text-gray-400 font-medium uppercase tracking-wider">Payout</p>
                    <p className="text-xl font-bold text-gray-800 tabular-nums leading-tight">
                      {payoutLabel}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-gray-100" />

        {/* Details grid */}
        <div className="p-6">
          <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Details</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Provider */}
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-gray-50">
              <span className={`text-lg bg-gradient-to-br ${provider.gradient} text-white w-9 h-9 rounded-lg flex items-center justify-center shadow-sm`}>
                {provider.icon}
              </span>
              <div>
                <p className="text-[11px] text-gray-400 font-medium uppercase tracking-wider">Provider</p>
                <p className="text-sm font-semibold text-gray-800">{provider.label}</p>
              </div>
            </div>

            {/* Expires */}
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-gray-50">
              <span className="text-gray-400 w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center">
                <FaCalendarAlt className="w-4 h-4" />
              </span>
              <div>
                <p className="text-[11px] text-gray-400 font-medium uppercase tracking-wider">Expires</p>
                <p className="text-sm font-semibold text-gray-800">{formatDate(reward.expiresAt)}</p>
              </div>
            </div>

            {/* Created */}
            <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-gray-50">
              <span className="text-gray-400 w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center">
                <FaCalendarAlt className="w-4 h-4" />
              </span>
              <div>
                <p className="text-[11px] text-gray-400 font-medium uppercase tracking-wider">Created</p>
                <p className="text-sm font-semibold text-gray-800">{formatDate(reward.createdAt)}</p>
              </div>
            </div>

            {/* Tremendous Product */}
            {tremendousMetadata && (
              <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-gray-50">
                <span className="text-gray-400 w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center">
                  <FaTag className="w-4 h-4" />
                </span>
                <div className="min-w-0">
                  <p className="text-[11px] text-gray-400 font-medium uppercase tracking-wider">Tremendous Product</p>
                  <p className="text-sm font-semibold text-gray-800 truncate">{tremendousMetadata.productNameSnapshot}</p>
                  <p className="text-xs text-gray-400 font-mono mt-0.5">{tremendousMetadata.productId}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Countries section */}
        {countries.length > 0 && (
          <>
            <div className="border-t border-gray-100" />
            <div className="p-6">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider flex items-center gap-2">
                  <FaGlobeAmericas className="w-3.5 h-3.5" />
                  Available Countries
                  <span className="text-[11px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-semibold">
                    {countries.length}
                  </span>
                </h2>
              </div>

              <div className="flex flex-wrap gap-2">
                {visibleCountries.map((code) => (
                  <span
                    key={code}
                    className="inline-flex items-center px-2.5 py-1 rounded-lg bg-gray-50 border border-gray-200 text-xs font-medium text-gray-600"
                  >
                    {code}
                  </span>
                ))}
              </div>

              {hasMoreCountries && (
                <button
                  onClick={() => setShowAllCountries(!showAllCountries)}
                  className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-brand-500 hover:text-brand-600 transition-colors cursor-pointer"
                >
                  {showAllCountries ? (
                    <>
                      Show less <FaChevronUp className="w-2.5 h-2.5" />
                    </>
                  ) : (
                    <>
                      Show {countries.length - MAX_COUNTRIES_VISIBLE} more <FaChevronDown className="w-2.5 h-2.5" />
                    </>
                  )}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default RewardDetailsPage;
